// The header's right-hand cluster, adapted from design/isomorphic/src/layouts/header-actions.tsx
// (Isomorphic's header-menu-right, notification-dropdown and profile-menu): colour mode, what is
// waiting for you, the language (S113) and the profile menu with sign-out.
import { BellSimpleRinging, Moon, Sun } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { ActionIcon, Dropdown } from "rizzui";

import { meQuery, signOutAndLeave } from "../auth/api";
import { useT } from "../i18n";
import { setMode, useMode } from "../theme/mode";
import { HeaderLanguageMenu, LanguageOptions } from "../ui/LanguageMenu";
import { Avatar } from "../ui/Avatar";
import { cn } from "../ui/cn";
import { NotificationsPanel } from "./NotificationsPanel";
import { useAttention } from "./attention";

const round = "h-[34px] w-[34px] rounded-full bg-gray-100 text-gray-700 hover:text-gray-1000 dark:bg-gray-100";

export function ThemeToggle() {
  const t = useT().shell.header;
  const mode = useMode();
  const next = mode === "dark" ? "light" : "dark";
  return (
    <ActionIcon aria-label={next === "light" ? t.toLight : t.toDark} variant="text" className={round} onClick={() => setMode(next)}>
      {mode === "dark" ? <Sun weight="duotone" className="h-[18px] w-[18px]" /> : <Moon weight="duotone" className="h-[18px] w-[18px]" />}
    </ActionIcon>
  );
}

function NotificationsBell() {
  const t = useT().shell.header;
  const attention = useAttention();
  const count = attention.questions.length + attention.pendingDevices.length;
  return (
    <Dropdown placement="bottom-end">
      <Dropdown.Trigger aria-label={count > 0 ? t.waiting(count) : t.nothingWaiting} className={cn(round, "relative flex items-center justify-center")}>
        <BellSimpleRinging weight="duotone" className="h-[18px] w-[18px]" />
        {count > 0 ? (
          <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-orange px-1 text-[10px] font-bold text-white dark:text-gray-0">
            {count}
          </span>
        ) : null}
      </Dropdown.Trigger>
      <Dropdown.Menu className="w-[340px] !p-0 dark:bg-gray-100">
        <NotificationsPanel attention={attention} />
      </Dropdown.Menu>
    </Dropdown>
  );
}

function ProfileMenu() {
  const { shell, common } = useT();
  const t = shell.header;
  const me = useQuery(meQuery());
  const navigate = useNavigate();
  const email = me.data?.email ?? "";
  return (
    <Dropdown placement="bottom-end">
      <Dropdown.Trigger aria-label={t.profile} className="rounded-full">
        <Avatar email={email} />
      </Dropdown.Trigger>
      <Dropdown.Menu className="w-64 dark:bg-gray-100">
        <div className="px-3 py-2">
          <div className="truncate font-semibold text-gray-900">{email}</div>
          <div className="text-xs capitalize text-gray-500">{me.data?.role}</div>
        </div>
        <Dropdown.Item className="rounded-md" onClick={() => void navigate("/account")}>
          {t.accountAndPassword}
        </Dropdown.Item>
        <Dropdown.Item className="rounded-md" onClick={() => void navigate("/devices")}>
          {t.myDevices}
        </Dropdown.Item>
        <div className="mt-1 border-t border-muted px-3 pb-1 pt-2 text-xs font-medium text-gray-500">{common.language.label}</div>
        <LanguageOptions />
        <div className="my-1 border-t border-muted" />
        <Dropdown.Item className="rounded-md text-red" onClick={() => void signOutAndLeave()}>
          {t.signOut}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export function HeaderActions() {
  return (
    <div className="ms-auto flex shrink-0 items-center gap-2 sm:gap-3">
      <HeaderLanguageMenu />
      <ThemeToggle />
      <NotificationsBell />
      <ProfileMenu />
    </div>
  );
}
